import axios, { AxiosResponse } from 'axios'
import type { Report, UploadResponse } from '../types/report'
import { useAuthStore } from '../stores/auth'

export class ReportService {
  private baseURL: string

  constructor() {
    const env: any = (import.meta as any).env || {}
    this.baseURL = env.VITE_API_URL || env.VITE_API_BASE_URL || 'http://localhost:8010'
  }

  /**
   * Obtener la URL base del servicio
   */
  public getBaseURL(): string {
    return this.baseURL
  }

  private async request<T>(config: any): Promise<T> {
    try {
      const authStore = useAuthStore()
      const headers = {
        ...config.headers,
        'ngrok-skip-browser-warning': 'true',
        ...(authStore.accessToken && { 'Authorization': `Bearer ${authStore.accessToken}` })
      }

      const response: AxiosResponse<T> = await axios({
        baseURL: this.baseURL,
        timeout: 30000,
        ...config,
        headers
      })
      return response.data
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data.message || 'API Error')
      } else if (error.request) {
        throw new Error('Network Error - Please check your connection')
      } else {
        throw new Error('Request failed')
      }
    }
  }

  async uploadReport(file: File): Promise<Report> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.request<UploadResponse>({
      method: 'POST',
      url: '/api/interpret',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    if (!response.success) {
      throw new Error(response.message)
    }

    // Get the full report data
    return await this.getReport(response.reportId)
  }

  async uploadReportV2(file: File): Promise<Report> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.request<UploadResponse>({
      method: 'POST',
      url: '/api/interpret_v2',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    if (!response.success) {
      throw new Error(response.message)
    }

    // Get the full report data
    return await this.getReport(response.reportId)
  }

  async uploadReportV2WithSSE(
    file: File, 
    onProgress?: (progress: { step: number; agent: string; status: string }) => void
  ): Promise<Report> {
    const formData = new FormData()
    formData.append('file', file)

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const authStore = useAuthStore()
      
      xhr.open('POST', `${this.baseURL}/api/interpret_v2_sse`)
      
      // Agregar token de autenticación
      if (authStore.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${authStore.accessToken}`)
      }
      
      let reportId: string = ''
      let buffer = ''
      
      xhr.onprogress = () => {
        const lines = xhr.responseText.substring(buffer.length).split('\n\n')
        buffer = xhr.responseText
        
        console.log('📡 SSE Data recibido:', xhr.responseText.substring(buffer.length))
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6))
              console.log('📦 SSE Event parseado:', data)
              
              if (data.type === 'report_id') {
                reportId = data.reportId
                console.log('🆔 Report ID:', reportId)
              } else if (data.type === 'progress' && onProgress) {
                console.log('🔄 Enviando progreso al callback:', data)
                onProgress({
                  step: data.step,
                  agent: data.agent,
                  status: data.status
                })
              } else if (data.type === 'complete') {
                console.log('✅ Procesamiento completado')
                // Obtener el reporte completo
                this.getReport(data.reportId)
                  .then(report => resolve(report))
                  .catch(err => reject(err))
              } else if (data.type === 'error') {
                console.error('❌ Error en SSE:', data.message)
                reject(new Error(data.message))
              }
            } catch (e) {
              console.log('⚠️ Error parsing SSE chunk:', e)
              // Ignorar errores de parsing en chunks incompletos
            }
          }
        }
      }
      
      xhr.onerror = () => {
        reject(new Error('Network error'))
      }
      
      xhr.onload = () => {
        if (xhr.status !== 200) {
          reject(new Error(`HTTP Error: ${xhr.status}`))
        }
      }
      
      xhr.send(formData)
    })
  }

  async uploadReportV2WithPolling(
    file: File, 
    onProgress?: (progress: { step: number; agent: string; status: string }) => void
  ): Promise<Report> {
    const formData = new FormData()
    formData.append('file', file)

    // Iniciar procesamiento
    const response = await this.request<{ success: boolean; reportId: string; message: string }>({
      method: 'POST',
      url: '/api/interpret_v2_polling',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    if (!response.success || !response.reportId) {
      throw new Error('Failed to start processing')
    }

    const reportId = response.reportId
    console.log('🆔 Report ID:', reportId)

    // Polling para obtener progreso
    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          const progressResponse = await this.request<{
            success: boolean;
            progress: any;
            status: string;
          }>({
            method: 'GET',
            url: `/api/reports/${reportId}/progress`
          })

          if (progressResponse.success && progressResponse.progress) {
            const progress = progressResponse.progress
            console.log('📊 Progreso recibido:', progress)

            if (onProgress) {
              onProgress({
                step: progress.currentStep,
                agent: progress.currentAgent,
                status: progress.status
              })
            }

            // Si está completado, obtener el reporte final
            if (progressResponse.status === 'completed') {
              clearInterval(pollInterval)
              try {
                const finalReport = await this.getReport(reportId)
                resolve(finalReport)
              } catch (err) {
                reject(err)
              }
            } else if (progressResponse.status === 'failed') {
              clearInterval(pollInterval)
              reject(new Error('Processing failed'))
            }
          }
        } catch (error) {
          console.error('Error polling progress:', error)
          clearInterval(pollInterval)
          reject(error)
        }
      }, 3000) // Poll cada 3 segundos (evita error 429)

      // Timeout después de 5 minutos
      setTimeout(() => {
        clearInterval(pollInterval)
        reject(new Error('Processing timeout'))
      }, 300000)
    })
  }

  async getReport(reportId: string): Promise<Report> {
    const response = await this.request<{ success: boolean; data: Report }>({
      method: 'GET',
      url: `/api/reports/${reportId}`
    })

    if (!response.success) {
      throw new Error('Failed to get report')
    }

    return response.data
  }

  async downloadReportPDF(reportId: string): Promise<Blob> {
    const authStore = useAuthStore()
    const url = `${this.baseURL}/api/reports/${reportId}/pdf`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(authStore.accessToken ? { 'Authorization': `Bearer ${authStore.accessToken}` } : {}),
        'ngrok-skip-browser-warning': 'true'
      }
    })
    if (!response.ok) {
      throw new Error('No se pudo generar el PDF')
    }
    return await response.blob()
  }

  async listReports(page: number = 1, limit: number = 10, status?: string): Promise<{ reports: Report[], pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    })

    if (status) {
      params.append('status', status)
    }

    const response = await this.request<{
      success: boolean
      data: {
        reports: Report[]
        pagination: any
      }
    }>({
      method: 'GET',
      url: `/api/reports?${params.toString()}`
    })

    if (!response.success) {
      throw new Error('Failed to list reports')
    }

    return {
      reports: response.data.reports,
      pagination: response.data.pagination
    }
  }

  async getHealthStatus(): Promise<any> {
    return await this.request({
      method: 'GET',
      url: '/health'
    })
  }
}
